import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';

import { TranslatePipe } from '@recruitee/i18n';
import type { OfferPromoteJobBoardsHotspot } from '@recruitee/integrations-types';
import type { DialogComponentI } from '@recruitee/user-interface';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  RtStatefulComponent,
} from '@recruitee/user-interface';

import { CurrentHotspotToken } from '../../../client/integrations-client.module';
import { Destroyable, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import type { CreatePostingForm, Posting } from '../types';
import { WerkenInEenHotelApiService } from '../werken-in-een-hotel-api';
import {
  initializeEmptyPostingFormGroup,
  WerkenInEenHotelPostingFormComponent,
} from '../werken-in-een-hotel-posting-form/werken-in-een-hotel-posting-form.component';

type State = {
  creating: boolean;
  errors: string[];
  errorFields: Record<string, string[]>;
};

type DialogData = {};

@Component({
  selector: 'rti-werken-in-een-hotel-create-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './werken-in-een-hotel-create-posting-dialog.component.html',
  styleUrls: ['./werken-in-een-hotel-create-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    WerkenInEenHotelPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class WerkenInEenHotelCreatePostingDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  public postingForm: UntypedFormGroup;

  constructor(
    public dialog: DialogRef,
    cdRef: ChangeDetectorRef,
    private werkenInEenHotelApi: WerkenInEenHotelApiService,
    private store: Store,
    @Inject(CurrentHotspotToken)
    private currentHotspot: OfferPromoteJobBoardsHotspot,
  ) {
    super(cdRef);

    this.postingForm = initializeEmptyPostingFormGroup();

    this.createState({
      creating: false,
      errors: [],
      errorFields: {},
    });
  }

  public ngOnInit(): void {
    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public setDialogData(_data: DialogData): void {}

  public publish(): void {
    this.setState({ ...this.state, creating: true });

    this.werkenInEenHotelApi.post(`posting`, this.createPostingFormData).subscribe(
      (_data: { posting: Posting }) => {
        this.setState({ ...this.state, creating: false });

        this.store.dispatch(
          IntegrationEventActions.WerkenInEenHotelPostingCreated({
            payload: { context: { hotspot: this.currentHotspot } },
          }),
        );

        this.dialog.close({ success: true });
      },
      ({ error }) => {
        const errorFields = error.errorFields;
        if (errorFields && (errorFields.mainVacancyPhoto || errorFields.photos)) {
          this.setState({ ...this.state, creating: false, errorFields });
        } else {
          this.setState({ ...this.state, creating: false, errors: error.error });
        }
      },
    );
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return this.postingForm.valid && this.postingForm.value.mainVacancyPhoto;
  }

  private get createPostingFormData(): FormData {
    const form: CreatePostingForm = {
      offerId: this.currentHotspot.params.offerId,
      locationId: this.postingForm.value.locationId,
      contactId: this.postingForm.value.contactId,
      functionCharacteristicId: this.postingForm.value.functionCharacteristicId,
      employmentIds: this.postingForm.value.employmentIds,
      mainFunctionCategoryId: this.postingForm.value.mainFunctionCategoryId,
      extraFunctionCategoryId: this.postingForm.value.extraFunctionCategoryId || undefined,
      publishOnOtherJobBoards: this.postingForm.value.publishOnOtherJobBoards,
      mainVacancyPhoto: this.postingForm.value.mainVacancyPhoto,
      additionalPhotos: this.postingForm.value.additionalPhotosToAdd,
      youtubeUrl: this.postingForm.value.youtubeUrl || undefined,
    };

    const formData = new FormData();
    formData.append('offerId', form.offerId.toString());
    formData.append('contactId', form.contactId.toString());
    formData.append('locationId', form.locationId.toString());
    formData.append('functionCharacteristicId', form.functionCharacteristicId);
    formData.append('mainFunctionCategoryId', form.mainFunctionCategoryId);
    if (form.extraFunctionCategoryId) {
      formData.append('extraFunctionCategoryId', form.extraFunctionCategoryId);
    }
    formData.append('publishOnOtherJobBoards', form.publishOnOtherJobBoards.toString());

    form.employmentIds.forEach(id => {
      formData.append('employmentIds[]', id);
    });

    if (form.youtubeUrl) {
      formData.append('youtubeUrl', form.youtubeUrl);
    }

    formData.append('mainVacancyPhoto', form.mainVacancyPhoto);

    form.additionalPhotos.forEach(photo => {
      formData.append('photos[]', photo);
    });

    return formData;
  }
}
